import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import FormWrapper from "../../components/bundles/FormWrapper";
import { paymentMethods } from "../../components/bundles/reservations/utils/PaymentsOptions";
import InsertDate from "../../components/reservation_v2/InsertDate";
import ReservationForm from "../../components/reservation_v2/form/ReservationForm";
import useCan from "../../components/users/permissions/useCan";
import useFetch from "../../hooks/useFetch";
import { IReservationFormSchemaV2 } from "../../interfaces/reservations/Reservations.types";
import { getReservationsDetails } from "../../services/Reservations.service";
import { getReservationPayment } from "../../services/Wallet.service";
import { ReservationSchema } from "../../utils/validations/Reservation.schema";

const ReservationEdit = () => {
  const methods = useForm<IReservationFormSchemaV2>({
    resolver: yupResolver(ReservationSchema),
    shouldFocusError: false
  });
  const { id } = useParams();

  const { loading, response, error, refetch } = useFetch(
    getReservationsDetails,
    {
      initRequest: true,
      params: { uniqueJourneyId: id || "" }
    }
  );

  // Gated on the endpoint's own permission: a 401 would read as "this booking used no credits".
  const canReadCredits = useCan("wallet.credit.read");
  const { response: payment } = useFetch(getReservationPayment, {
    initRequest: canReadCredits,
    params: { uniqueJourneyId: id || "" }
  });

  useEffect(() => {
    if (response)
      methods.reset({
        uniqueJourneyId: response.data.uniqueJourneyId,
        email: response.data.clientNames?.[0]?.email || "",
        phoneNumber: response.data.clientNames?.[0]?.phoneNumber || "",
        oneWayTicketDetails: response.data.oneWayTicketDetails,
        roundWayTicketDetails: response.data.roundWayTicketDetails,
        // The fallback keeps an unmapped method id visible and the form submittable.
        paymentMethod: response.data.paymentMethodId
          ? paymentMethods.find(
              (el) => el.value === response.data.paymentMethodId
            ) ?? {
              label: `Metoda ${response.data.paymentMethodId}`,
              value: response.data.paymentMethodId
            }
          : { label: "None", value: 0 }
      });
  }, [response]);

  return (
    <FormProvider {...methods}>
      <FormWrapper
        // The route carries the booking reference; the history is keyed by the row's numeric id.
        history={{ entityName: "JourneyReservation", entityId: response?.data.id }}
        showPrompt={
          !methods.formState.isSubmitSuccessful &&
          !!Object.keys(methods.formState.dirtyFields).length
        }
        backTitle="Kthehu tek rezervimet"
        backTo={"/ticket/reservations"}
        backState={["Rezervimet", "Version 2.0"]}
        error={!!error}
        mode={"edit"}
        response={response}
        loading={loading}
        notFoundMessage="Rezervimi nuk u gjet"
        title={"Modifiko rezervimin"}
        rightHeaderContent={
          <InsertDate insertDate={response?.data.insertDate || ""} />
        }
      >
        <>
          {response?.data ? (
            <ReservationForm
              details={response?.data}
              onSelected={refetch}
              breakdown={payment?.data}
            />
          ) : null}
        </>
      </FormWrapper>
    </FormProvider>
  );
};

export default ReservationEdit;
